/**
 * Auth0 Configuration
 *
 * Environment variables:
 * - REACT_APP_AUTH0_ENABLED: Feature flag to enable/disable Auth0
 * - REACT_APP_AUTH0_DOMAIN: Auth0 tenant domain (e.g., your-tenant.auth0.com)
 * - REACT_APP_AUTH0_CLIENT_ID: Auth0 SPA application client ID
 * - REACT_APP_AUTH0_REDIRECT_URI: Callback URL after Auth0 authentication
 */

export const auth0Config = {
  domain: process.env.REACT_APP_AUTH0_DOMAIN || '',
  clientId: process.env.REACT_APP_AUTH0_CLIENT_ID || '',
  redirectUri: process.env.REACT_APP_AUTH0_REDIRECT_URI || `${window.location.origin}/auth/callback`,
  isEnabled: process.env.REACT_APP_AUTH0_ENABLED === 'true',
};

/**
 * Check if Auth0 is properly configured and enabled.
 * Returns true only if the feature flag is enabled AND required config is present.
 */
export const isAuth0Configured = (): boolean => {
  return auth0Config.isEnabled && !!auth0Config.domain && !!auth0Config.clientId;
};

/**
 * Decode a JWT payload without verification.
 * Used to read claims from PA-issued tokens.
 */
const decodeJwtPayload = (token: string): Record<string, unknown> | null => {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const payload = parts[1];
    const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(decoded);
  } catch {
    return null;
  }
};

/**
 * Check if the user authenticated via Auth0 based on the PA access token.
 * Returns true if the token contains auth_method: 'auth0' claim.
 */
export const wasAuthenticatedViaAuth0 = (accessToken: string | null | undefined): boolean => {
  if (!accessToken) return false;
  const payload = decodeJwtPayload(accessToken);
  return payload?.auth_method === 'auth0';
};
